import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X, Printer } from 'lucide-react';
import { PresentationLogo } from './PresentationLogo';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
import type { SlideId } from './slides/SlideShell';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  useEffect(() => {
    if (printMode) {
      // Give the DOM time to finish all Framer Motion entrance animations
      // and load images over the network before triggering the print dialog.
      const timer = setTimeout(() => {
        window.print();
      }, 2500);
      const handleAfterPrint = () => setPrintMode(false);
      window.addEventListener('afterprint', handleAfterPrint);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }
  }, [printMode]);
  useEffect(() => {
    if (printMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Let the playable Snak (sliders, buttons) keep its own keyboard input.
      const target = e.target as HTMLElement | null;
      // Popups (e.g. the concept proposal) own the keyboard while open.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (target?.closest('[data-snak-device]') || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, printMode]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const navigateTo = (id: SlideId) => {
    const idx = slideTitles.findIndex(s => s.id === id);
    if (idx >= 0 && idx !== currentIndex) goTo(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  if (printMode) {
    return <>
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center hide-on-print" data-id="element-85" data-mp="1zgcumxq4ycme">
          <div className="w-12 h-12 border-4 border-[var(--brand)]/20 border-t-[var(--brand)] rounded-full animate-spin mb-4" data-id="element-86" data-mp="29gdj0y2v3ixg" />
          <p className="text-xl font-bold text-[var(--fg)] animate-pulse" data-id="element-87" data-mp="2n70g6lsz50rf">
            Preparing PDF...
          </p>
          <p className="text-sm text-[var(--muted)] mt-2" data-id="element-88" data-mp="2j5ey8m1l1wad">
            Waiting for animations and images to load
          </p>
        </div>
        <div className="print-container bg-white" data-id="element-89" data-mp="2x3llz9ctifwb">
          {slides.map((Slide, idx) => <div key={idx} className="print-slide w-full h-full relative overflow-hidden break-after-page" data-id="element-90" data-mp="2s51zn7ubb23f" data-mpi={idx}>
              <Slide index={idx} total={slides.length} data-id="element-91" data-mp="xtvmsixfc7yv" />
              <PresentationLogo data-id="element-92" data-mp="xv6ma6ju6tk6" />
            </div>)}
        </div>
      </>;
  }
  const CurrentSlide = slides[currentIndex];
  return <div className="deck-root relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-93" data-mp="1482m67uosf4q">
      <AnimatePresence mode="wait" custom={direction} data-id="element-94" data-mp="197x7lvvp0cie">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-95" data-mp="196ciils5dlrx">
          <CurrentSlide index={currentIndex} total={slides.length} onNavigate={navigateTo} data-id="element-96" data-mp="va8s73ke9xkq" />
        </motion.div>
      </AnimatePresence>

      <PresentationLogo data-id="element-97" data-mp="2q0ei42n99of" />

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40 hide-on-print" data-id="element-98" data-mp="2g2nzw5746561">
        <motion.div className="h-full bg-[var(--brand)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-99" data-mp="2c09h1l3g35er" />
      </div>

      {/* Top left controls */}
      <div className="absolute top-3 left-3 z-50 flex items-center overflow-hidden rounded-full border border-black/10 bg-white/90 shadow-sm backdrop-blur hide-on-print" data-id="element-100" data-mp="1x4bqqjg7cvag">
        <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="flex h-8 items-center gap-1.5 px-3.5 text-[var(--brand)] transition-colors hover:bg-[var(--surface)]" data-id="element-101" data-mp="1oap017quqi84">
          <List className="h-3.5 w-3.5" data-id="element-102" data-mp="1ecj0uxwfjq6y" />
          <span className="text-xs font-semibold" data-id="element-103" data-mp="19e7p2hmkr5m3">Contents</span>
        </button>

        <span className="h-4 w-px bg-black/10" aria-hidden="true" data-id="element-104" data-mp="19gad4w462oo6" />

        <button onClick={() => setPrintMode(true)} aria-label="Export the deck to PDF" className="flex h-8 items-center gap-1.5 px-3.5 text-[var(--brand)] transition-colors hover:bg-[var(--surface)]" data-id="element-105" data-mp="zor0bvb55u6x">
          <Printer className="h-3.5 w-3.5" data-id="element-106" data-mp="ur914wmxetc4" />
          <span className="text-xs font-semibold" data-id="element-107" data-mp="us0kn93910m1">PDF</span>
        </button>
      </div>

      {/* Bottom nav cluster */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-108" data-mp="1ihlfoqcofobo">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-109" data-mp="1nfeid9eru077">
          <ChevronLeft className="h-4 w-4" data-id="element-110" data-mp="266nu7pc0l11y" />
        </button>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-111" data-mp="2688t1r5d365m">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-112" data-mp="3lxq36jocv1pf">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-lighter)] text-xs font-medium" data-id="element-113" data-mp="4hrpsomgia8g">
            / {slides.length}
          </span>
        </div>
        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-114" data-mp="3vo1lxccp7fgd">
          <ChevronRight className="h-4 w-4" data-id="element-115" data-mp="3cw0e3khy4eut" />
        </button>
      </div>

      {/* Collapsible TOC */}
      <AnimatePresence data-id="element-116" data-mp="x4upvl6s6t0t">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-117" data-mp="s72wemqhmyui" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-118" data-mp="30g0on3xmxkkc">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-119" data-mp="1k2ak5w2lsua6">
                <div data-id="element-120" data-mp="1p1df2novdk4f">
                  <p className="text-[var(--brand)] text-xs font-semibold uppercase tracking-wider" data-id="element-121" data-mp="1hfrrmsdq7v7l">
                    Ferring × Snakker · REKOVELLE
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-122" data-mp="1cmyiztf502q8">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-123" data-mp="12rdm29oxdsnc">
                  <X className="h-5 w-5" data-id="element-124" data-mp="1qgg25d3wvdfq" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-id="element-125" data-mp="1qi9dh5pgylu2">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-126" data-mp="ez6o563jxdlt" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-127" data-mp="305jbzhd9izn1">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-128" data-mp="3j22cjia03f7z">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-light)]' : 'text-[var(--muted)]'}`} data-id="element-129" data-mp="3j1sldrd7z20p">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-130" data-mp="1jqi0m0tholhj">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}